import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import { useAppContext } from '../context/AppContext';

const RSSHeadlines = ({ maxHeadlines = 5 }) => {
  const [headlines, setHeadlines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { setCurrentTab } = useAppContext();

  useEffect(() => {
    fetchHeadlines();
  }, []);

  const fetchHeadlines = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('rss_headlines')
        .select('*')
        .order('pub_date', { ascending: false })
        .limit(maxHeadlines);
      
      if (error) throw error;
      setHeadlines(data || []);
    } catch (err) {
      console.error('Error fetching headlines:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Decode HTML entities and convert to plain text or JSX
  const decodeHTMLEntities = (text) => {
    if (!text) return text;
    
    // Create a textarea element to decode entities
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  };

  // Parse HTML and render it safely
  const renderHTMLContent = (html) => {
    if (!html) return '';
    
    // Decode HTML entities first
    const decoded = decodeHTMLEntities(html);
    
    // For now, just return decoded text (strip HTML tags for display)
    return decoded.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  };

  const extractSource = (link) => {
    if (!link) return '';
    try {
      // Check if it's a Google Alerts redirect URL with &url= parameter
      if (link.includes('google.com/url') && link.includes('&url=')) {
        const urlMatch = link.match(/[&?]url=([^&]+)/);
        if (urlMatch) {
          const decodedUrl = decodeURIComponent(urlMatch[1]);
          const actualUrl = new URL(decodedUrl);
          const hostname = actualUrl.hostname.replace('www.', '');
          return hostname.split('.')[0].split(/[-_]/).map(word => 
            word.charAt(0).toUpperCase() + word.slice(1)
          ).join(' ');
        }
      }
      
      // Fallback to direct URL parsing
      const url = new URL(link);
      const hostname = url.hostname.replace('www.', '');
      return hostname.split('.')[0].split(/[-_]/).map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
    } catch {
      return '';
    }
  };

  const truncateText = (text, maxLength = 120) => {
    if (!text || text.length <= maxLength) return text;
    // Strip HTML tags for display
    const plainText = text.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    if (plainText.length <= maxLength) return plainText;
    const truncated = plainText.slice(0, maxLength);
    const lastSpace = truncated.lastIndexOf(' ');
    return truncated.slice(0, lastSpace > 0 ? lastSpace : maxLength) + '...';
  };

  const getDisplayText = (headline) => {
    // Prefer content over summary for better snippets
    return headline.content || headline.summary || '';
  };

  const getImpactCategory = (score) => {
    if (score === null || score === undefined) return null;
    if (score >= 80) return { label: 'High Impact', color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' };
    if (score >= 40) return { label: 'Moderate', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' };
    return { label: 'General Interest', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600 dark:text-gray-400 text-sm">Loading headlines...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-600 dark:text-red-400 text-sm py-2">
        Error loading headlines: {error}
      </div>
    );
  }

  if (headlines.length === 0) {
    return (
      <div className="text-gray-500 dark:text-gray-400 text-sm py-2">
        No headlines available
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-600 pb-1">
        Recent News
      </h3>
      <div className="space-y-4">
        {headlines.map((headline) => (
          <div key={headline.id} className="group">
            <a
              href={headline.link}
              target="_blank"
              rel="noopener noreferrer"
              className="block hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg p-3 -m-3 transition-colors duration-200"
            >
              {/* Title and Impact Badge */}
              <div className="flex items-start justify-between gap-3 mb-2">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200 leading-tight flex-1">
                  {renderHTMLContent(headline.title)}
                </h4>
                {headline.impact_score !== null && headline.impact_score !== undefined && (
                  <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full whitespace-nowrap ${getImpactCategory(headline.impact_score).color}`}>
                    {getImpactCategory(headline.impact_score).label}
                  </span>
                )}
              </div>
              
              {/* Source */}
              <div className="text-xs font-medium text-blue-600 dark:text-blue-400 mb-2">
                {extractSource(headline.link)}
              </div>
              
              {/* Summary with ellipsis */}
              {getDisplayText(headline) && (
                <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                  {truncateText(getDisplayText(headline))}
                </p>
              )}
            </a>
          </div>
        ))}
        {/* More... Link */}
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
          <button
            onClick={() => setCurrentTab('Headlines')}
            className="text-sm font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors duration-200"
          >
            More... →
          </button>
        </div>
      </div>
    </div>
  );
};

export default RSSHeadlines;